import React, { useMemo, useState } from 'react';
import { Card, InputNumber, Select, Space, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { useGetQaqcPipePipesize } from '../../../../hooks/genericHooks';
import StandardTable from '../../../../components/common/StandardTable';
import type { QaqcPipePipesize } from '../../../../types';
import Button from '../../../../components/common/Button';

const QaqcPipePipesizeList: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetQaqcPipePipesize();
  const rows: QaqcPipePipesize[] = Array.isArray(data?.data) ? (data!.data as unknown as QaqcPipePipesize[]) : [];

  // Filters
  const [filterName, setFilterName] = useState<string | undefined>();
  const [minInch, setMinInch] = useState<number | undefined>();
  const [maxInch, setMaxInch] = useState<number | undefined>();

  const nameOptions = useMemo(() => Array.from(new Set(rows.map(r => r.pipesize_name))).sort()
    .map(v => ({ value: v, label: v })), [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterName && r.pipesize_name !== filterName) return false;
      if (minInch !== undefined && Number(r.pipesize_inch) < minInch) return false;
      if (maxInch !== undefined && Number(r.pipesize_inch) > maxInch) return false;
      return true;
    });
  }, [rows, filterName, minInch, maxInch]);

  const columns = useMemo(
    () => [
      { title: 'Pipe Size', dataIndex: 'pipesize_name', key: 'pipesize_name' },
      { title: 'Inch', dataIndex: 'pipesize_inch', key: 'pipesize_inch' },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        fixed: 'right',
        render: (_: any, rec: QaqcPipePipesize) => (
          <Space>
            <Tooltip title="View">
              <Button action="read" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/qaqc/piping/tools/pipesizes/view/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
            <Tooltip title="Edit">
              <Button action="edit" type="primary" size="small" icon={<EditOutlined />} onClick={() => navigate(`/qaqc/piping/tools/pipesizes/edit/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
          </Space>
        )
      }
    ],
    []
  );

  return (
    <Card
      title="Pipe Sizes"
      extra={
        <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qaqc/piping/tools/pipesizes/create')}>
          New Pipe Size
        </Button>
      }
    >
      {error ? (
        <div style={{ color: 'red', marginBottom: 12 }}>Failed to load Pipe Sizes: {String((error as any)?.message || error)}</div>
      ) : null}
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          placeholder="Filter by Size Code"
          style={{ width: 220 }}
          options={nameOptions}
          value={filterName}
          onChange={setFilterName}
          optionFilterProp="label"
        />
        <InputNumber placeholder="Min Inch" style={{ width: 140 }} value={minInch} onChange={(v) => setMinInch(typeof v === 'number' ? v : undefined)} />
        <InputNumber placeholder="Max Inch" style={{ width: 140 }} value={maxInch} onChange={(v) => setMaxInch(typeof v === 'number' ? v : undefined)} />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => { setFilterName(undefined); setMinInch(undefined); setMaxInch(undefined); }}
        >
          Reset Filters
        </Button>
      </Space>
      <StandardTable
        loading={isLoading}
        columns={columns as any}
        dataSource={filtered}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </Card>
  );
};

export default QaqcPipePipesizeList;
